import type { PaymentIntent } from '@Types/PaymentIntent.interface';
import { PaymentIntentStatus } from '@Types/PaymentIntent.interface';

export interface MppPaymentFormData {
    name: string;
    billingAddress: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface MppPaymentCardElement {
    stripeElement?: object;
}

export interface MppStripeInstance {
    confirmCardPayment(
        clientSecret: string,
        data: {
            payment_method: {
                card: object;
                billing_details: {
                    name: string;
                    address: {
                        line1: string;
                        city: string;
                        state: string;
                        postal_code: string;
                    };
                    email?: string;
                };
            };
            receipt_email?: string;
        }
    ): Promise<{ paymentIntent?: object; error?: { message: string } }>;
}

export interface MppPaymentApis {
    registerPaymentIntent(intent: PaymentIntent): Promise<PaymentIntent>;
    getUpdatedIntent(intent: PaymentIntent): Promise<PaymentIntent>;
}

export const processPayment = async ({
    form,
    recipientEmail,
    insurancePolicyId,
    isAboveMaxPolicy,
    stripe,
    cardElement,
    apis,
}: {
    form: MppPaymentFormData;
    recipientEmail: string;
    insurancePolicyId: string;
    isAboveMaxPolicy: boolean;
    stripe: MppStripeInstance;
    cardElement: object;
    apis: MppPaymentApis;
}): Promise<{ success: boolean; errorMessage: string | null }> => {
    const intent = await apis.registerPaymentIntent({
        customerEmail: recipientEmail,
        insurancePolicyId,
        isAboveMaxPolicy,
    });

    if (intent.status !== PaymentIntentStatus.Registered) {
        return { success: false, errorMessage: 'We couldn\'t set up your payment because our payment system is temporarily unavailable. Please wait a moment and click Confirm again.' };
    }

    if (!intent.id || !intent.gatewayClientSecret) {
        return { success: false, errorMessage: 'We couldn\'t complete your payment setup because our payment system returned an incomplete response. Please wait a moment and click Confirm again. If this keeps happening, contact support.' };
    }

    const normalizedForm = {
        name: form.name.trim(),
        billingAddress: form.billingAddress.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        zipCode: form.zipCode.trim(),
    };

    const paymentResult = await stripe.confirmCardPayment(intent.gatewayClientSecret, {
        payment_method: {
            card: cardElement,
            billing_details: {
                name: normalizedForm.name,
                address: {
                    line1: normalizedForm.billingAddress,
                    city: normalizedForm.city,
                    state: normalizedForm.state,
                    postal_code: normalizedForm.zipCode,
                },
                email: recipientEmail,
            },
        },
        receipt_email: recipientEmail,
    });

    if (paymentResult.error) {
        const msg = (paymentResult.error.message ?? '').trim();
        const separator = msg.endsWith('.') ? ' ' : '. ';
        return {
            success: false,
            errorMessage: `Your payment could not be processed: ${msg}${separator}Please check your card details and try again.`,
        };
    }

    let updatedIntent;
    try {
        updatedIntent = await apis.getUpdatedIntent({
            id: intent.id,
            customerEmail: intent.customerEmail,
            insurancePolicyId: intent.insurancePolicyId,
            isAboveMaxPolicy: intent.isAboveMaxPolicy,
        });
    } catch (err) {
        console.error('getUpdatedIntent failed after card was charged — status:', (err as any)?.response?.status);
        return {
            success: false,
            errorMessage: 'Your card was charged but we couldn\'t confirm the payment with our system. Please do not submit another payment. Contact support to verify your payment status.',
        };
    }

    if (updatedIntent.status !== PaymentIntentStatus.CaptureComplete) {
        return {
            success: false,
            errorMessage: 'Your card was charged but we couldn\'t confirm the payment with our system. Please do not submit another payment. Contact support to verify your payment status.',
        };
    }

    return { success: true, errorMessage: null };
};

